import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { MovementSystem } from '../Controls/MovementSystem.js';

export const SceneControls = forwardRef(({ isLocked, onLock, onUnlock, onViewModeToggle, isViewMode, spawnPoint = null }, ref) => {
    const { camera, gl, scene } = useThree();
    const movementSystem = useRef(null);

    // Initialize movement system
    useEffect(() => {
        if (!camera || !gl.domElement) {
            console.error('Missing required elements for controls');
            return;
        }

        console.log('Initializing movement system with:', {
            camera,
            domElement: gl.domElement,
            isViewMode
        });

        const system = new MovementSystem({
            camera,
            domElement: gl.domElement,
            scene,
            spawnPoint
        });

        // Set initial view mode
        system.setViewMode(isViewMode);
        
        // Enable the movement system
        system.enable();
        
        movementSystem.current = system;

        return () => {
            if (movementSystem.current) {
                movementSystem.current.dispose();
            }
        };
    }, [camera, gl, isViewMode, spawnPoint]);

    // Update movement each frame
    useFrame((state, delta) => {
        if (movementSystem.current) {
            movementSystem.current.update(delta);
        }
    });

    // Handle view mode changes
    useEffect(() => {
        if (movementSystem.current) {
            movementSystem.current.setViewMode(isViewMode);
            // Ensure it's enabled when in view mode
            if (isViewMode) {
                movementSystem.current.enable();
            }
        }
    }, [isViewMode]);

    // Expose camera control methods
    useImperativeHandle(ref, () => ({
        camera,
        movementSystem: movementSystem.current,
        lock: () => movementSystem.current?.lock(),
        unlock: () => movementSystem.current?.unlock()
    }));

    return null;
}); 